import { Suspense, useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ArchiveRedirect from "@/components/ArchiveRedirect";
import { AuthProvider } from "@/hooks/useAuth";
import { TestingProvider } from "@/contexts/TestingContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageLoader, AdminPageLoader } from "@/components/PageLoader";

// Eagerly loaded pages (critical path)
import NotFound from "./pages/NotFound";
import { SupportChat } from "./components/SupportChat";

import { AnalyticsTracking } from "./components/AnalyticsTracking";
import { RouteTracker } from "./components/RouteTracker";
import { FunnelEventDebugger } from "./components/FunnelEventDebugger";
import { InternalUTMInterceptor } from "./components/InternalUTMInterceptor";
import { useUTMCapture } from "@/hooks/useUTMTracking";

/** Runs useUTMCapture on every route change globally */
const GlobalUTMCapture = () => { useUTMCapture(); return null; };

// Lazy loaded May pages (main site - previously eager)
const MayIndex = lazyWithRetry(() => import("./pages/may/Index"));
const MayMixtape = lazyWithRetry(() => import("./pages/may/Mixtape"));
const MayTickets = lazyWithRetry(() => import("./pages/may/Tickets"));
const MayAccommodations = lazyWithRetry(() => import("./pages/may/Accommodations"));
const CheckoutAddons = lazyWithRetry(() => import("./pages/may/CheckoutAddons"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const AdminNotFound = lazyWithRetry(() => import("./pages/admin/AdminNotFound"));
const AdminRouteShell = lazyWithRetry(() => import("./components/admin/AdminRouteShell"));

// Lazy loaded admin pages
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/Dashboard"));
const AdminRegistrations = lazyWithRetry(() => import("./pages/admin/Registrations"));
const AdminEvents = lazyWithRetry(() => import("./pages/admin/Events"));
const AdminGuestLists = lazyWithRetry(() => import("./pages/admin/GuestLists"));
const AdminTickets = lazyWithRetry(() => import("./pages/admin/Tickets"));
const AdminSales = lazyWithRetry(() => import("./pages/admin/Sales"));
const AdminEmails = lazyWithRetry(() => import("./pages/admin/Emails"));
const AdminWebhooks = lazyWithRetry(() => import("./pages/admin/Webhooks"));
const AdminSurveys = lazyWithRetry(() => import("./pages/admin/Surveys"));
const AdminReminders = lazyWithRetry(() => import("./pages/admin/Reminders"));
const SystemHealthPage = lazyWithRetry(() => import("./pages/admin/SystemHealth"));
const AdminIncidents = lazyWithRetry(() => import("./pages/admin/Incidents"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/Settings"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/AdminUsers"));
const AdminWaitlist = lazyWithRetry(() => import("./pages/admin/Waitlist"));
const AdminAccommodationWaitlist = lazyWithRetry(() => import("./pages/admin/AccommodationWaitlist"));
const AdminAccommodationZones = lazyWithRetry(() => import("./pages/admin/AccommodationZones"));
const AdminFamilyStyleUnits = lazyWithRetry(() => import("./pages/admin/FamilyStyleUnits"));
const AdminLodgingOperations = lazyWithRetry(() => import("./pages/admin/LodgingOperations"));
const AdminUpgrades = lazyWithRetry(() => import("./pages/admin/Upgrades"));
const AdminAbandonedRecovery = lazyWithRetry(() => import("./pages/admin/AbandonedRecovery"));
const AdminPreviewSignups = lazyWithRetry(() => import("./pages/admin/PreviewSignups"));
const AdminInventory = lazyWithRetry(() => import("./pages/admin/Inventory"));
const AdminAddons = lazyWithRetry(() => import("./pages/admin/Addons"));
const AdminCustomOffers = lazyWithRetry(() => import("./pages/admin/CustomOffers"));
const AdminCustomers = lazyWithRetry(() => import("./pages/admin/Customers"));
const AdminCustomerDetail = lazyWithRetry(() => import("./pages/admin/CustomerDetail"));
const AdminStyleGuide = lazyWithRetry(() => import("./pages/admin/StyleGuide"));
const AdminVolunteerInterests = lazyWithRetry(() => import("./pages/admin/VolunteerInterests"));
// Universal Pipeline Page
const PipelinePage = lazyWithRetry(() =>
  import("@/components/pipeline/PipelinePage").then((module) => ({ default: module.PipelinePage })),
);
const AdminPacing = lazyWithRetry(() => import("./pages/admin/Pacing"));
const AdminInbox = lazyWithRetry(() => import("./pages/admin/Inbox"));
const AdminSocialPublishing = lazyWithRetry(() => import("./pages/admin/SocialPublishing"));
const AdminDeadLetterQueue = lazyWithRetry(() => import("./pages/admin/DeadLetterQueue"));
const AdminScheduledJobs = lazyWithRetry(() => import("./pages/admin/ScheduledJobs"));
const AdminEmailDelivery = lazyWithRetry(() => import("./pages/admin/EmailDelivery"));
const AdminTeam = lazyWithRetry(() => import("./pages/admin/Team"));
const AdminCheckoutHealth = lazyWithRetry(() => import("./pages/admin/CheckoutHealth"));
const AdminCrewBids = lazyWithRetry(() => import("./pages/admin/CrewBids"));
const AdminMarketingAssets = lazyWithRetry(() => import("./pages/admin/MarketingAssets"));
const AdminLeads = lazyWithRetry(() => import("./pages/admin/Leads"));
const AdminPaymentPlans = lazyWithRetry(() => import("./pages/admin/PaymentPlans"));
const AdminPromoCodes = lazyWithRetry(() => import("./pages/admin/PromoCodes"));
const AdminPromoCodeStatus = lazyWithRetry(() => import("./pages/admin/PromoCodeStatus"));
const AdminPromoCodeInsights = lazyWithRetry(() => import("./pages/admin/PromoCodeInsights"));
const AdminFunnel = lazyWithRetry(() => import("./pages/admin/Funnel"));
const AdminEventPhotos = lazyWithRetry(() => import("./pages/admin/EventPhotos"));

// Lazy loaded public pages (less critical)
const AdminSetup = lazyWithRetry(() => import("./pages/AdminSetup"));
const LandingPage = lazyWithRetry(() => import("./pages/LandingPage"));
const AnalogPage = lazyWithRetry(() => import("./pages/AnalogPage"));
const CheckIn = lazyWithRetry(() => import("./pages/CheckIn"));
const CheckInScanner = lazyWithRetry(() => import("./pages/CheckInScanner"));
const PrintableCheckIn = lazyWithRetry(() => import("./pages/PrintableCheckIn"));
const CheckInGuide = lazyWithRetry(() => import("./pages/CheckInGuide"));
const BoxOfficeScanner = lazyWithRetry(() => import("./pages/BoxOfficeScanner"));
const StationScanner = lazyWithRetry(() => import("./pages/StationScanner"));
const BoxOfficeManifest = lazyWithRetry(() => import("./pages/BoxOfficeManifest"));
const DoorList = lazyWithRetry(() => import("./pages/DoorList"));
const AdminBoxOffice = lazyWithRetry(() => import("./pages/admin/BoxOffice"));
// GuestListDetail removed — comp tickets are managed via Tickets and Guest Lists pages
const PrintableManifest = lazyWithRetry(() => import("./pages/PrintableManifest"));
const PrintableDinnerManifest = lazyWithRetry(() => import("./pages/PrintableDinnerManifest"));
const FridayDinner = lazyWithRetry(() => import("./pages/may/FridayDinner"));
const MyTickets = lazyWithRetry(() => import("./pages/MyTickets"));
const ConfirmTransfer = lazyWithRetry(() => import("./pages/ConfirmTransfer"));
const UndoTransfer = lazyWithRetry(() => import("./pages/UndoTransfer"));
const CustomOffer = lazyWithRetry(() => import("./pages/CustomOffer"));
const CustomOfferSuccess = lazyWithRetry(() => import("./pages/CustomOfferSuccess"));
const ContractSigning = lazyWithRetry(() => import("./pages/ContractSigning"));
const LodgingOffer = lazyWithRetry(() => import("./pages/offers/LodgingOffer"));
const PackageOffer = lazyWithRetry(() => import("./pages/offers/PackageOffer"));
const PackageOfferAccommodations = lazyWithRetry(() => import("./pages/offers/PackageOfferAccommodations"));
const LodgingOfferSuccess = lazyWithRetry(() => import("./pages/offers/LodgingOfferSuccess"));

// Lazy loaded May pages (non-critical)
const MayStay = lazyWithRetry(() => import("./pages/may/Stay"));
const MayLineup = lazyWithRetry(() => import("./pages/may/Lineup"));
const MayExperience = lazyWithRetry(() => import("./pages/may/Experience"));
const MayEat = lazyWithRetry(() => import("./pages/may/Eat"));
const MayStory = lazyWithRetry(() => import("./pages/may/Story"));
const MayFAQ = lazyWithRetry(() => import("./pages/may/FAQ"));
const MayTerms = lazyWithRetry(() => import("./pages/may/Terms"));
const MayPrivacy = lazyWithRetry(() => import("./pages/may/Privacy"));
const MayContact = lazyWithRetry(() => import("./pages/may/Contact"));
const MayGetInvolved = lazyWithRetry(() => import("./pages/may/GetInvolved"));
const MayLodging = lazyWithRetry(() => import("./pages/may/Lodging"));
const MayLodgingSuccess = lazyWithRetry(() => import("./pages/may/LodgingSuccess"));
const MayAccommodationsInvite = lazyWithRetry(() => import("./pages/may/AccommodationsInvite"));
const PreviewAccess = lazyWithRetry(() => import("./pages/may/PreviewAccess"));
const MayGather = lazyWithRetry(() => import("./pages/may/Gather"));
const MayWin = lazyWithRetry(() => import("./pages/may/Win"));
const MayGiveawayRules = lazyWithRetry(() => import("./pages/may/GiveawayRules"));
const PaymentPlanStatus = lazyWithRetry(() => import("./pages/may/PaymentPlanStatus"));
const MayAlmostHere = lazyWithRetry(() => import("./pages/may/AlmostHere"));
const MayGettingHere = lazyWithRetry(() => import("./pages/may/GettingHere"));
const MaySchedule = lazyWithRetry(() => import("./pages/may/Schedule"));
const PaymentPlanReview = lazyWithRetry(() => import("./pages/may/PaymentPlanReview"));
const CartReview = lazyWithRetry(() => import("./pages/may/CartReview"));
// Loaded eagerly: the Wine Camp archive must render without depending on a
// separate chunk fetch, which could leave the route stuck on "Loading...".
import WineCampLineup from "./pages/may/WineCampLineup";
const WineCampWinery = lazyWithRetry(() => import("./pages/may/WineCampWinery"));
const FoodVendorPage = lazyWithRetry(() => import("./pages/may/FoodVendorPage"));
const Sauna = lazyWithRetry(() => import("./pages/may/Sauna"));
const SaunaVendorPage = lazyWithRetry(() => import("./pages/may/SaunaVendorPage"));
const Bar = lazyWithRetry(() => import("./pages/may/Bar"));
const MayPhotos = lazyWithRetry(() => import("./pages/may/Photos"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - data stays fresh longer since event switching is rare
      gcTime: 1000 * 60 * 30, // 30 minutes - keep unused data in cache longer
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch when component remounts if data is fresh
      refetchOnReconnect: false, // Don't auto-refetch on reconnect
    },
  },
});

// Wrapper for admin routes with Suspense
const AdminRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<AdminPageLoader />}>
    <AdminRouteShell>{children}</AdminRouteShell>
  </Suspense>
);

// Wrapper for public lazy routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TestingProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
          <AnalyticsTracking />
          <GlobalUTMCapture />
          <RouteTracker />
          <InternalUTMInterceptor />
          <SupportChat />
          
          <FunnelEventDebugger />
          <ErrorBoundary>
            <Routes>
              {/* Main Public Routes - May/Analog site at root */}
              <Route path="/" element={<LazyRoute><MayIndex /></LazyRoute>} />
              <Route path="/tickets" element={<LazyRoute><MayTickets /></LazyRoute>} />
              {/* Post-event: every checkout route is closed and sends visitors to the historical page. */}
              <Route path="/tickets/payment-plan" element={<Navigate to="/tickets" replace />} />
              <Route path="/checkout" element={<ArchiveRedirect to="/tickets" />} />
              <Route path="/checkout/lodging" element={<Navigate to="/stay" replace />} />
              <Route path="/checkout/addons" element={<Navigate to="/tickets" replace />} />
              <Route path="/checkout/review" element={<Navigate to="/tickets" replace />} />
              <Route path="/accommodations" element={<Navigate to="/stay" replace />} />
              <Route path="/ticket-success" element={<ArchiveRedirect to="/my-tickets" />} />
              {/* Legacy lodging routes: historical information only */}
              <Route path="/lodging" element={<Navigate to="/stay" replace />} />
              <Route path="/lodging/success" element={<Navigate to="/accommodations/success" replace />} />
              <Route path="/lodging/invite" element={<Navigate to="/stay" replace />} />
              <Route path="/accommodations/invite" element={<Navigate to="/stay" replace />} />
              <Route path="/accommodations/success" element={<LazyRoute><MayLodgingSuccess /></LazyRoute>} />

              <Route path="/stay" element={<LazyRoute><MayStay /></LazyRoute>} />
              <Route path="/lineup" element={<LazyRoute><MayLineup /></LazyRoute>} />
              <Route path="/experience" element={<LazyRoute><MayExperience /></LazyRoute>} />
              <Route path="/story" element={<LazyRoute><MayStory /></LazyRoute>} />
              {/* Post-event: retired acquisition pages go straight to the closeout homepage. */}
              <Route path="/gather" element={<ArchiveRedirect to="/" />} />
              <Route path="/real" element={<ArchiveRedirect to="/" />} />
              <Route path="/escape" element={<ArchiveRedirect to="/" />} />
              {/* Post-event: the 2026 giveaway is over; no entry can be accepted. */}
              <Route path="/win" element={<ArchiveRedirect to="/" />} />
              <Route path="/giveaway-rules" element={<LazyRoute><MayGiveawayRules /></LazyRoute>} />
              <Route path="/faq" element={<LazyRoute><MayFAQ /></LazyRoute>} />
              {/* Known soft-404 routes resolved to the closest preserved content */}
              <Route path="/faqs" element={<Navigate to="/faq" replace />} />
              <Route path="/path" element={<Navigate to="/experience" replace />} />
              <Route path="/showclothes" element={<Navigate to="/experience" replace />} />
              <Route path="/submissions" element={<Navigate to="/get-involved" replace />} />

              <Route path="/mixtape" element={<LazyRoute><MayMixtape /></LazyRoute>} />
              <Route path="/terms" element={<LazyRoute><MayTerms /></LazyRoute>} />
              <Route path="/privacy" element={<LazyRoute><MayPrivacy /></LazyRoute>} />
              <Route path="/contact" element={<LazyRoute><MayContact /></LazyRoute>} />
              <Route path="/get-involved" element={<LazyRoute><MayGetInvolved /></LazyRoute>} />
              <Route path="/bringyourcrew" element={<ArchiveRedirect to="/tickets" />} />
              <Route path="/bringyourcrew/checkout" element={<ArchiveRedirect to="/tickets" />} />
              <Route path="/crew" element={<ArchiveRedirect to="/" />} />

              <Route path="/payment-plan-status" element={<LazyRoute><PaymentPlanStatus /></LazyRoute>} />
              <Route path="/reserve" element={<ArchiveRedirect to="/tickets" />} />
              <Route path="/almost-here" element={<LazyRoute><MayAlmostHere /></LazyRoute>} />
              <Route path="/getting-here" element={<LazyRoute><MayGettingHere /></LazyRoute>} />
              <Route path="/schedule" element={<LazyRoute><MaySchedule /></LazyRoute>} />
              <Route path="/winecamp" element={<WineCampLineup />} />
              <Route path="/winecamp/:slug" element={<LazyRoute><WineCampWinery /></LazyRoute>} />
              <Route path="/eat" element={<LazyRoute><MayEat /></LazyRoute>} />
              <Route path="/eat/:slug" element={<LazyRoute><FoodVendorPage /></LazyRoute>} />
              <Route path="/sauna" element={<LazyRoute><Sauna /></LazyRoute>} />
              <Route path="/sauna/:slug" element={<LazyRoute><SaunaVendorPage /></LazyRoute>} />
              <Route path="/bar" element={<LazyRoute><Bar /></LazyRoute>} />
              <Route path="/fielddayca" element={<LazyRoute><FridayDinner /></LazyRoute>} />
              <Route path="/photos" element={<LazyRoute><MayPhotos /></LazyRoute>} />
              <Route path="/auth" element={<LazyRoute><Auth /></LazyRoute>} />
              
              {/* Utility public pages */}
              <Route path="/survey" element={<ArchiveRedirect to="/my-tickets" />} />
              <Route path="/simple-admin-signup" element={<ArchiveRedirect to="/" />} />
              <Route path="/signup-test" element={<ArchiveRedirect to="/" />} />
              <Route path="/admin-setup" element={<LazyRoute><AdminSetup /></LazyRoute>} />
              <Route path="/upgrade-success" element={<ArchiveRedirect to="/my-tickets" />} />
              <Route path="/qa/checkout-funnel" element={<ArchiveRedirect to="/" />} />
              <Route path="/go" element={<ArchiveRedirect to="/" />} />
              <Route path="/access" element={<ArchiveRedirect to="/" />} />
              <Route path="/analog" element={<LazyRoute><AnalogPage /></LazyRoute>} />
              
              {/* Sessions (free event RSVPs) */}
              <Route path="/sessions/analogxh2hotel" element={<ArchiveRedirect to="/" />} />
              
              {/* Admin Routes with Sidebar Layout (lazy loaded) */}
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/registrations" element={<AdminRoute><AdminRegistrations /></AdminRoute>} />
              <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
              <Route path="/admin/customers/:email" element={<AdminRoute><AdminCustomerDetail /></AdminRoute>} />
              <Route path="/admin/events" element={<AdminRoute><AdminEvents /></AdminRoute>} />
              <Route path="/admin/guest-lists" element={<AdminRoute><AdminGuestLists /></AdminRoute>} />
              <Route path="/admin/tickets" element={<AdminRoute><AdminTickets /></AdminRoute>} />
              <Route path="/admin/sales" element={<AdminRoute><AdminSales /></AdminRoute>} />
              <Route path="/admin/emails" element={<AdminRoute><AdminEmails /></AdminRoute>} />
              <Route path="/admin/webhooks" element={<AdminRoute><AdminWebhooks /></AdminRoute>} />
              <Route path="/admin/surveys" element={<AdminRoute><AdminSurveys /></AdminRoute>} />
              <Route path="/admin/reminders" element={<AdminRoute><AdminReminders /></AdminRoute>} />
              <Route path="/admin/health" element={<AdminRoute><SystemHealthPage /></AdminRoute>} />
              <Route path="/admin/incidents" element={<AdminRoute><AdminIncidents /></AdminRoute>} />
              <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/waitlist" element={<AdminRoute><AdminWaitlist /></AdminRoute>} />
              <Route path="/admin/accommodation-waitlist" element={<AdminRoute><AdminAccommodationWaitlist /></AdminRoute>} />
              <Route path="/admin/lodging" element={<AdminRoute><AdminLodgingOperations /></AdminRoute>} />
              <Route path="/admin/lodging/zones" element={<AdminRoute><AdminAccommodationZones /></AdminRoute>} />
              <Route path="/admin/lodging/units" element={<AdminRoute><AdminFamilyStyleUnits /></AdminRoute>} />
              <Route path="/admin/lodging/operations" element={<AdminRoute><AdminLodgingOperations /></AdminRoute>} />
              <Route path="/admin/lodging/family-style" element={<AdminRoute><AdminFamilyStyleUnits /></AdminRoute>} />
              <Route path="/admin/upgrades" element={<AdminRoute><AdminUpgrades /></AdminRoute>} />
              <Route path="/admin/abandoned-recovery" element={<AdminRoute><AdminAbandonedRecovery /></AdminRoute>} />
              <Route path="/admin/preview-signups" element={<AdminRoute><AdminPreviewSignups /></AdminRoute>} />
              <Route path="/admin/inventory" element={<AdminRoute><AdminInventory /></AdminRoute>} />
              <Route path="/admin/offers" element={<AdminRoute><AdminCustomOffers /></AdminRoute>} />
              <Route path="/admin/addons" element={<AdminRoute><AdminAddons /></AdminRoute>} />
              <Route path="/admin/style-guide" element={<AdminRoute><AdminStyleGuide /></AdminRoute>} />
              <Route path="/admin/volunteers" element={<AdminRoute><AdminVolunteerInterests /></AdminRoute>} />
              <Route path="/admin/volunteer-interests" element={<AdminRoute><AdminVolunteerInterests /></AdminRoute>} />
              <Route path="/admin/production-volunteers" element={<AdminRoute><PipelinePage slug="volunteer" /></AdminRoute>} />
              {/* Universal Pipeline Routes */}
              <Route path="/admin/artists" element={<AdminRoute><PipelinePage slug="artist" /></AdminRoute>} />
              <Route path="/admin/vendors" element={<AdminRoute><PipelinePage slug="vendor" /></AdminRoute>} />
              <Route path="/admin/artisans" element={<AdminRoute><PipelinePage slug="artisan" /></AdminRoute>} />
              <Route path="/admin/partners" element={<AdminRoute><PipelinePage slug="partner" /></AdminRoute>} />
              <Route path="/admin/street-team" element={<AdminRoute><PipelinePage slug="street_team" /></AdminRoute>} />
              <Route path="/admin/winecamp" element={<AdminRoute><PipelinePage slug="winecamp" /></AdminRoute>} />
              <Route path="/admin/pacing" element={<AdminRoute><AdminPacing /></AdminRoute>} />
              <Route path="/admin/inbox" element={<AdminRoute><AdminInbox /></AdminRoute>} />
              <Route path="/admin/crew-bids" element={<AdminRoute><AdminCrewBids /></AdminRoute>} />
              <Route path="/admin/social" element={<AdminRoute><AdminSocialPublishing /></AdminRoute>} />
              <Route path="/admin/team" element={<AdminRoute><AdminTeam /></AdminRoute>} />
              <Route path="/admin/dead-letter" element={<AdminRoute><AdminDeadLetterQueue /></AdminRoute>} />
              <Route path="/admin/jobs" element={<AdminRoute><AdminScheduledJobs /></AdminRoute>} />
              <Route path="/admin/email-delivery" element={<AdminRoute><AdminEmailDelivery /></AdminRoute>} />
              <Route path="/admin/checkout-health" element={<AdminRoute><AdminCheckoutHealth /></AdminRoute>} />
              <Route path="/admin/marketing-assets" element={<AdminRoute><AdminMarketingAssets /></AdminRoute>} />
              <Route path="/admin/leads" element={<AdminRoute><AdminLeads /></AdminRoute>} />
              <Route path="/admin/payment-plans" element={<AdminRoute><AdminPaymentPlans /></AdminRoute>} />
              <Route path="/admin/promo-codes" element={<AdminRoute><AdminPromoCodes /></AdminRoute>} />
              <Route path="/admin/promo-codes/status" element={<AdminRoute><AdminPromoCodeStatus /></AdminRoute>} />
              <Route path="/admin/promo-codes/insights" element={<AdminRoute><AdminPromoCodeInsights /></AdminRoute>} />
              <Route path="/admin/funnel" element={<AdminRoute><AdminFunnel /></AdminRoute>} />
              <Route path="/admin/event-photos" element={<AdminRoute><AdminEventPhotos /></AdminRoute>} />
              
              {/* Admin 404 catch-all */}
              <Route path="/admin/*" element={<AdminRoute><AdminNotFound /></AdminRoute>} />
              
              {/* Other Admin Pages */}
              <Route path="/admin/checkin" element={<LazyRoute><CheckIn /></LazyRoute>} />
              <Route path="/admin/box-office" element={<AdminRoute><AdminBoxOffice /></AdminRoute>} />
              {/* Old guest list detail route removed — comps managed on Guest Lists page */}
              
              {/* Public Check-in Pages */}
              <Route path="/check-in" element={<LazyRoute><CheckInGuide /></LazyRoute>} />
              {/* Legacy scanner deprecated — Box Office is the single per-ticket scanner */}
              <Route path="/check-in-scanner" element={<Navigate to="/box-office" replace />} />
              <Route path="/box-office" element={<LazyRoute><BoxOfficeScanner /></LazyRoute>} />
              <Route path="/box-office/manifest" element={<LazyRoute><BoxOfficeManifest /></LazyRoute>} />
              <Route path="/box-office/door-list" element={<LazyRoute><DoorList /></LazyRoute>} />
              <Route path="/station" element={<LazyRoute><StationScanner /></LazyRoute>} />
              <Route path="/printable-checkin" element={<LazyRoute><PrintableCheckIn /></LazyRoute>} />
              <Route path="/printable-manifest" element={<LazyRoute><PrintableManifest /></LazyRoute>} />
              <Route path="/printable-dinner-manifest" element={<LazyRoute><PrintableDinnerManifest /></LazyRoute>} />
              
              {/* Ticket Holder Account */}
              <Route path="/my-tickets" element={<LazyRoute><MyTickets /></LazyRoute>} />
              <Route path="/confirm-transfer" element={<LazyRoute><ConfirmTransfer /></LazyRoute>} />
              <Route path="/undo-transfer" element={<LazyRoute><UndoTransfer /></LazyRoute>} />
              <Route path="/sign-contract" element={<LazyRoute><ContractSigning /></LazyRoute>} />
              {/* Post-event: all offer/purchase links are closed */}
              <Route path="/offer/:token" element={<Navigate to="/" replace />} />
              <Route path="/offer/:token/success" element={<LazyRoute><CustomOfferSuccess /></LazyRoute>} />
              <Route path="/offer/lodging" element={<Navigate to="/stay" replace />} />
              <Route path="/offer/lodging/success" element={<LazyRoute><LodgingOfferSuccess /></LazyRoute>} />
              <Route path="/offer/package" element={<Navigate to="/stay" replace />} />
              <Route path="/offer/package/accommodations" element={<Navigate to="/stay" replace />} />
              <Route path="/offer/package/success" element={<LazyRoute><LodgingOfferSuccess /></LazyRoute>} />

              
              {/* Legacy /may routes - redirect to root for backwards compatibility */}
              <Route path="/may/access" element={<LazyRoute><PreviewAccess /></LazyRoute>} />
              <Route path="/may" element={<Navigate to="/" replace />} />
              <Route path="/may/stay" element={<Navigate to="/stay" replace />} />
              <Route path="/may/lineup" element={<Navigate to="/lineup" replace />} />
              <Route path="/may/experience" element={<Navigate to="/experience" replace />} />
              <Route path="/may/story" element={<Navigate to="/story" replace />} />
              <Route path="/may/faq" element={<Navigate to="/faq" replace />} />
              <Route path="/may/terms" element={<Navigate to="/terms" replace />} />
              <Route path="/may/privacy" element={<Navigate to="/privacy" replace />} />
              <Route path="/may/contact" element={<Navigate to="/contact" replace />} />
              <Route path="/may/get-involved" element={<Navigate to="/get-involved" replace />} />
              <Route path="/may/tickets" element={<Navigate to="/tickets" replace />} />
              <Route path="/may/accommodations" element={<Navigate to="/stay" replace />} />
              <Route path="/may/ticket-success" element={<ArchiveRedirect to="/my-tickets" />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </ErrorBoundary>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </TestingProvider>
  </QueryClientProvider>
);

export default App;
